'use client';

import React, { useEffect, useState } from 'react';

export type ToastType = 'success' | 'error';

export interface ToastItemProps {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
  onDismiss: (id: string) => void;
}

const styles: Record<ToastType, { bg: string; border: string; icon: string; iconPath: string }> = {
  success: {
    bg: 'bg-emerald-500/95 dark:bg-emerald-600/95',
    border: 'border-emerald-400/50',
    icon: 'text-emerald-100',
    iconPath: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  },
  error: {
    bg: 'bg-red-500/95 dark:bg-red-600/95',
    border: 'border-red-400/50',
    icon: 'text-red-100',
    iconPath: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  },
};

export const ToastItem: React.FC<ToastItemProps> = ({
  id,
  type,
  message,
  duration = 5000,
  onDismiss,
}) => {
  const [isExiting, setIsExiting] = useState(false);
  const style = styles[type];

  useEffect(() => {
    const t = setTimeout(() => {
      setIsExiting(true);
      const t2 = setTimeout(() => onDismiss(id), 200);
      return () => clearTimeout(t2);
    }, duration);
    return () => clearTimeout(t);
  }, [id, duration, onDismiss]);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => onDismiss(id), 200);
  };

  return (
    <div
      role="alert"
      aria-live="polite"
      className={`
        flex items-start gap-3 min-w-[280px] max-w-[420px] px-4 py-3 rounded-xl border shadow-lg
        transition-all duration-200
        ${style.bg} ${style.border}
        ${isExiting ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'}
      `}
    >
      <span className={style.icon} aria-hidden>
        <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d={style.iconPath} />
        </svg>
      </span>
      <p className="flex-1 text-sm font-medium text-white leading-snug">{message}</p>
      <button
        type="button"
        onClick={handleDismiss}
        className="shrink-0 p-1 rounded-md text-white/80 hover:text-white hover:bg-white/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
        aria-label="Fechar notificação"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};
